import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, PackagePlus, Calculator, Boxes, Layers, MapPin, Warehouse, Wallet, Receipt, LifeBuoy, Settings, LogOut, ShieldCheck, Menu, X, AlertTriangle, Undo2, IndianRupee, Scale, ClipboardList, PackageOpen, BarChart3, Globe2, KeyRound, Plug, RotateCcw, FileStack } from "lucide-react";
import Logo from "./Logo";
import SessionBanner from "./SessionBanner";
import { useAuth } from "../context/AuthContext";
import api, { inr } from "../lib/api";

const SECTIONS = [
  { title: "", items: [{ to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    title: "SHIP", items: [
      { to: "/app/create-shipment", label: "Create Shipment", icon: PackagePlus },
      { to: "/app/rate-calculator", label: "Rate Calculator", icon: Calculator },
      { to: "/app/international", label: "International", icon: Globe2 },
      { to: "/app/shipments", label: "Shipments", icon: Boxes },
      { to: "/app/bulk-shipping", label: "Bulk Shipping", icon: Layers },
    ],
  },
  {
    title: "TRACK", items: [
      { to: "/app/track", label: "Track", icon: MapPin },
      { to: "/app/ndr", label: "NDR", icon: AlertTriangle },
      { to: "/app/shipments?status=RTO", label: "RTO", icon: RotateCcw },
      { to: "/app/returns", label: "Returns", icon: Undo2 },
    ],
  },
  {
    title: "OPERATIONS", items: [
      { to: "/app/warehouses", label: "Warehouses", icon: Warehouse },
      { to: "/app/warehouse-ops", label: "Warehouse Ops", icon: ClipboardList },
      { to: "/app/manifests", label: "Manifests", icon: FileStack },
      { to: "/app/inventory", label: "Inventory", icon: PackageOpen },
    ],
  },
  {
    title: "FINANCE", items: [
      { to: "/app/wallet", label: "Wallet", icon: Wallet },
      { to: "/app/cod", label: "COD", icon: IndianRupee },
      { to: "/app/billing", label: "Billing", icon: Receipt },
      { to: "/app/disputes", label: "Weight Disputes", icon: Scale },
    ],
  },
  { title: "INSIGHTS", items: [{ to: "/app/analytics", label: "Analytics", icon: BarChart3 }] },
  {
    title: "DEVELOPERS", items: [
      { to: "/app/developer", label: "API & Webhooks", icon: KeyRound },
      { to: "/app/integrations", label: "Integrations", icon: Plug },
    ],
  },
  { title: "SUPPORT", items: [{ to: "/app/support", label: "Tickets", icon: LifeBuoy }] },
  { title: "", items: [{ to: "/app/kyc", label: "KYC Verification", icon: ShieldCheck }, { to: "/app/settings", label: "Settings", icon: Settings }] },
];

export function SidebarNav({ sections, onNavigate }) {
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-4 py-5">
      {sections.map((sec, i) => (
        <div key={i}>
          {sec.title && <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{sec.title}</p>}
          <div className="space-y-1">
            {sec.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-shipany-blue text-white glow-blue" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                <item.icon className="h-4 w-4 shrink-0" /> {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  return (
    <div className="flex min-h-screen bg-shipany-mist" data-testid="customer-dashboard-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-shipany-navy lg:flex">
        <div className="px-5 py-5"><Logo dark /></div>
        <SidebarNav sections={SECTIONS} />
        <div className="border-t border-white/10 p-4">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="sidebar-admin-console" className="mb-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-shipany-cyan hover:bg-white/5">
              <ShieldCheck className="h-4 w-4" /> Admin Console
            </Link>
          )}
          <button onClick={doLogout} data-testid="sidebar-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-shipany-navy">
            <div className="flex items-center justify-between px-5 py-5">
              <Logo dark />
              <button onClick={() => setMobileOpen(false)} className="text-slate-400" data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
            </div>
            <SidebarNav sections={SECTIONS} onNavigate={() => setMobileOpen(false)} />
            <div className="border-t border-white/10 p-4">
              <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:text-white">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <SessionBanner />
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/85 px-5 py-3.5 backdrop-blur-xl">
          <button className="lg:hidden text-shipany-navy" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <Link to="/app/wallet" data-testid="topbar-wallet-balance" className="mono rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-100">
              {inr(wallet?.balance ?? 0)}
            </Link>
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-shipany-blue text-xs font-bold text-white">{(user?.name || "U")[0].toUpperCase()}</span>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-shipany-navy" data-testid="topbar-user-name">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-5 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
